import { requisicaoAutenticada } from './http';

/** A visão geral do Painel (D-015) — espelha `apps/api/src/modules/visao-geral`. */

export const SERVICOS = ['RECORRENTE', 'PONTUAL', 'PASSIVO'] as const;
export type Servico = (typeof SERVICOS)[number];

export const ETAPAS = ['SIMULACAO', 'NEGOCIACAO', 'FECHADO', 'CONCLUIDO', 'CANCELADO'] as const;
export type Etapa = (typeof ETAPAS)[number];

/** Sem etapa escolhida, o servidor considera o que foi de fato negociado. */
export const ETAPAS_PADRAO: readonly Etapa[] = ['FECHADO', 'CONCLUIDO'];

export const ROTULO_DO_SERVICO: Readonly<Record<Servico, string>> = {
  RECORRENTE: 'Recorrente',
  PONTUAL: 'Pontual',
  PASSIVO: 'Passivo',
};

export const ROTULO_DA_ETAPA: Readonly<Record<Etapa, string>> = {
  SIMULACAO: 'Simulação',
  NEGOCIACAO: 'Negociação',
  FECHADO: 'Fechado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

/** O que cada etapa junta, por serviço — a nota do filtro e do funil. */
export const AJUDA_DA_ETAPA: Readonly<Record<Etapa, string>> = {
  SIMULACAO: 'Recorrente só com cálculo; Pontual e Passivo em simulação.',
  NEGOCIACAO: 'Contrato do Recorrente aberto, em aprovação ou em assinatura.',
  FECHADO: 'Em execução: Recorrente fechado; Pontual fechado até transferido; Passivo ativo.',
  CONCLUIDO: 'Pontual pago; Passivo finalizado.',
  CANCELADO: 'Pontual e Passivo cancelados.',
};

/** A classe de cor de cada serviço nos gráficos — sempre a mesma, em qualquer filtro. */
export const COR_DO_SERVICO: Readonly<Record<Servico, string>> = {
  RECORRENTE: 'bg-serie-recorrente',
  PONTUAL: 'bg-serie-pontual',
  PASSIVO: 'bg-serie-passivo',
};

export interface FiltroDaVisaoGeral {
  /** `AAAA-MM`. */
  readonly de: string;
  readonly ate: string;
  readonly servicos: readonly Servico[];
  readonly etapas: readonly Etapa[];
  readonly clienteId: string;
  readonly fornecedorId: string;
  readonly assessorId: string;
  readonly tipoCredito: string;
}

export interface Valores {
  readonly creditoNegociado: number;
  readonly beneficioEconomico: number;
  readonly valorDosClientes: number;
  readonly spread: number;
  readonly operacoes: number;
}

export interface Recebimentos {
  readonly recebido: number;
  readonly aReceber: number;
  readonly emAtraso: number;
}

export interface Ranking extends Valores {
  readonly id: string | null;
  readonly nome: string;
}

export interface OperacaoResumo {
  readonly servico: Servico;
  readonly operacaoId: string;
  readonly clienteId: string;
  readonly clienteNome: string;
  readonly fornecedorNome: string | null;
  readonly assessorNome: string | null;
  readonly tipoCredito: string | null;
  readonly etapa: Etapa;
  readonly situacao: string;
  readonly de: string;
  readonly ate: string;
  readonly creditoNegociado: number;
  readonly beneficioEconomico: number;
  readonly valorDosClientes: number;
  readonly spread: number;
}

export interface OpcaoDeEmpresa {
  readonly businessId: string;
  readonly razaoSocial: string;
}

export interface VisaoGeral {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly totais: Valores & { readonly clientes: number };
  readonly recebimentos: Recebimentos;
  readonly porServico: readonly (Valores &
    Recebimentos & { readonly servico: Servico; readonly clientes: number })[];
  readonly porEtapa: readonly {
    readonly etapa: Etapa;
    readonly servico: Servico;
    readonly operacoes: number;
    readonly creditoNegociado: number;
  }[];
  readonly porMes: readonly {
    readonly mes: string;
    readonly servico: Servico;
    readonly creditoNegociado: number;
    readonly beneficioEconomico: number;
  }[];
  readonly porCliente: readonly Ranking[];
  readonly porFornecedor: readonly Ranking[];
  readonly porAssessor: readonly Ranking[];
  readonly porTipoCredito: readonly Ranking[];
  readonly operacoes: readonly OperacaoResumo[];
  readonly operacoesOmitidas: number;
  readonly filtros: {
    readonly clientes: readonly OpcaoDeEmpresa[];
    readonly fornecedores: readonly OpcaoDeEmpresa[];
    readonly assessores: readonly OpcaoDeEmpresa[];
    readonly tiposDeCredito: readonly string[];
  };
  readonly avisos: readonly string[];
  /** O detalhe do Recorrente — `null` quando ele está fora do filtro. */
  readonly recorrente: ResumoDoRecorrente | null;
}

export interface ResumoDoRecorrente {
  readonly clientes: number;
  readonly clientesComContratoFechado: number;
  readonly conversaoPercentual: number;
  readonly mesesAproveitados: number;
  readonly fila: {
    readonly vencidos: number;
    readonly falharam: number;
    readonly aguardandoEtapaAnterior: number;
    readonly agendados: number;
  };
  readonly linhas: readonly ClienteDoRecorrente[];
}

export interface ClienteDoRecorrente {
  readonly clienteId: string;
  readonly clienteNome: string;
  readonly assessorNome: string | null;
  readonly fornecedorNome: string | null;
  readonly etapa: Etapa;
  readonly situacao: string;
  readonly creditoNegociado: number;
  readonly beneficioEconomico: number;
  readonly valorDosClientes: number;
  readonly mesesAproveitados: number;
  readonly mesesComSped: number;
  readonly desagioMedioPercentual: number | null;
}

/** A query string do filtro: o que está no padrão não vai, para o servidor decidir. */
export function consultaDaVisaoGeral(f: FiltroDaVisaoGeral): string {
  const busca = new URLSearchParams({ de: f.de, ate: f.ate });
  if (f.servicos.length > 0 && f.servicos.length < SERVICOS.length) {
    busca.set('servicos', f.servicos.join(','));
  }
  // Nenhuma etapa marcada é "todas" na tela; para o servidor, ausente é o padrão
  // (fechado e concluído). Por isso vai a lista inteira, explícita.
  busca.set('etapas', (f.etapas.length === 0 ? ETAPAS : f.etapas).join(','));
  if (f.clienteId !== '') busca.set('clienteId', f.clienteId);
  if (f.fornecedorId !== '') busca.set('fornecedorId', f.fornecedorId);
  if (f.assessorId !== '') busca.set('assessorId', f.assessorId);
  if (f.tipoCredito !== '') busca.set('tipoCredito', f.tipoCredito);
  return busca.toString();
}

export function buscarVisaoGeral(f: FiltroDaVisaoGeral): Promise<VisaoGeral> {
  return requisicaoAutenticada<VisaoGeral>(`/visao-geral?${consultaDaVisaoGeral(f)}`);
}

/** Um exercício: de janeiro a dezembro do ano. */
export function periodoDoExercicio(ano: number): { de: string; ate: string } {
  return { de: `${ano}-01`, ate: `${ano}-12` };
}

/** Os atalhos de período, relativos a hoje. */
export function periodoDoAtalho(
  atalho: 'ULTIMOS_12' | 'ESTE_MES',
  hoje: Date = new Date(),
): { de: string; ate: string } {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth() + 1;
  const aaaamm = (a: number, m: number): string => `${a}-${String(m).padStart(2, '0')}`;
  if (atalho === 'ESTE_MES') return { de: aaaamm(ano, mes), ate: aaaamm(ano, mes) };
  const inicio = new Date(ano, mes - 12, 1);
  return { de: aaaamm(inicio.getFullYear(), inicio.getMonth() + 1), ate: aaaamm(ano, mes) };
}
